import { useTranslations } from 'next-intl'
import React from 'react'

import '@/styles/components/_order_quantity_form.scss'

interface OrderItem {
  id: string
  name: string
  price: number
  quantity: number
  maxQuantity: number
  minQuantity: number
  increase_num: number
  isGift?: boolean
  originalPrice?: number
  discountPercent?: number
}

interface GiftRule {
  id: number
  trigger_pd_id: string
  trigger_qty: number
  gift_pd_id: string
  gift_pd_name: string
  gift_price: number
  gift_sc?: number
  gift_tax?: number
  gift_qty: number
  gift_discount_percent: number
}

interface OrderQuantityFormProps {
  initialItems: OrderItem[]
  onQuantityChange: (updatedItems: OrderItem[]) => void
  giftRules?: GiftRule[]
}

const OrderQuantityForm: React.FC<OrderQuantityFormProps> = ({
  initialItems,
  onQuantityChange,
  giftRules = []
}) => {
  const t = useTranslations()

  // console.log('[GiftRules RAW from API]', JSON.stringify(giftRules, null, 2))
  // console.log('[initialItems RAW]', JSON.stringify(initialItems, null, 2))

  function applyGiftRules(baseItems: OrderItem[], rules: GiftRule[]): OrderItem[] {
    // Start with only non-gift items from baseItems
    const regularItems = baseItems.filter((it) => !it.isGift)
    const newGiftItems: OrderItem[] = []

    // Group rules by trigger product — for each trigger, only the best-matching rule wins
    const rulesByTrigger = new Map<string, GiftRule[]>()
    for (const rule of rules) {
      const key = String(rule.trigger_pd_id)
      if (!rulesByTrigger.has(key)) rulesByTrigger.set(key, [])
      rulesByTrigger.get(key)!.push(rule)
    }

    for (const [triggerId, triggerRules] of rulesByTrigger) {
      const trigger = regularItems.find((it) => String(it.id) === triggerId)
      if (!trigger) continue

      // Pick the rule with the highest trigger_qty that is still <= current quantity
      const applicable = triggerRules.filter((r) => trigger.quantity >= r.trigger_qty)
      if (applicable.length === 0) continue
      const bestRule = applicable.reduce((best, r) => r.trigger_qty > best.trigger_qty ? r : best)
      const multiplier = Math.floor(trigger.quantity / bestRule.trigger_qty)
      const giftQty = bestRule.gift_qty * multiplier

      // Apply discount on standard price, then add SC and VAT
      const afterDiscount = bestRule.gift_price * (1 - bestRule.gift_discount_percent / 100)
      const afterSC = afterDiscount * (1 + (bestRule.gift_sc || 0) / 100)
      const afterVAT = afterSC * (1 + (bestRule.gift_tax || 0) / 100)
      const discountedPrice = Math.round(afterVAT * 100) / 100

      // Original price with SC+VAT (no discount)
      const originalWithSCVAT = Math.round(
        bestRule.gift_price * (1 + (bestRule.gift_sc || 0) / 100) * (1 + (bestRule.gift_tax || 0) / 100) * 100
      ) / 100
      newGiftItems.push({
        id: bestRule.gift_pd_id,
        name: bestRule.gift_pd_name,
        price: discountedPrice,
        quantity: giftQty,
        maxQuantity: giftQty,
        minQuantity: giftQty,
        increase_num: 1,
        isGift: true,
        originalPrice: originalWithSCVAT,
        discountPercent: bestRule.gift_discount_percent
      })
    }

    return [...regularItems, ...newGiftItems]
  }

  const items = applyGiftRules(initialItems, giftRules)

  const handleQuantityChange = (index: number, newQuantity: number) => {
    const regularItems = items.filter((it) => !it.isGift)
    const maxAllowed = regularItems[index].maxQuantity
    const minAllowed = regularItems[index].minQuantity
    const validQuantity = Math.min(Math.max(minAllowed, newQuantity), maxAllowed)
    const updatedRegular = regularItems.map((it, i) =>
      i === index ? { ...it, quantity: validQuantity } : it
    )
    const merged = applyGiftRules(updatedRegular, giftRules)
    onQuantityChange(merged)
  }

  const regularItems = items.filter((it) => !it.isGift)
  const giftItems = items.filter((it) => it.isGift)

  const totalPrice =
    Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100

  return (
    <div className='order-quantity-form'>
      <div className='order-quantity-form__header'>
        <div className='w-1/2'>{t('Promotion Detail.column_order')}</div>
        <div className='w-1/4 text-center'>{t('Promotion Detail.column_quantity')}</div>
        <div className='w-1/4 text-right'>{t('Promotion Detail.column_price')}</div>
      </div>

      {regularItems.map((item, idx) => (
        <div
          key={item.id + idx}
          className={`order-quantity-form__item ${idx % 2 ? '' : ''}`}>
          <div className='order-quantity-form__name'>
            {item.name}
            <div className='mt-1 text-xs text-gray-500'>Max: {item.maxQuantity}</div>
          </div>
          <div className='order-quantity-form__quantity'>
            <select
              value={item.quantity}
              onChange={(e) => handleQuantityChange(idx, Number(e.target.value))}>
              {Array.from(
                {
                  length: Math.floor((item.maxQuantity - item.minQuantity) / item.increase_num) + 1
                },
                (_, i) => item.minQuantity + i * item.increase_num
              )
                .filter((q) => q <= item.maxQuantity)
                .map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
            </select>
          </div>
          <div className='order-quantity-form__price'>
            {item.price.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}{' '}
            {t('Promotion Detail.label_THB')}
          </div>
        </div>
      ))}

      {giftItems.map((item, idx) => (
        <div key={'gift-' + item.id + idx} className='order-quantity-form__item order-quantity-form__item--gift'>
          <div className='order-quantity-form__name'>
            {item.name}
            {item.discountPercent === 100 ? (
              <span className='order-quantity-form__badge order-quantity-form__badge--free'>FREE</span>
            ) : (
              <span className='order-quantity-form__badge order-quantity-form__badge--discount'>
                -{item.discountPercent}%
              </span>
            )}
          </div>
          <div className='order-quantity-form__quantity order-quantity-form__quantity--locked'>
            {item.quantity}
          </div>
          <div className='order-quantity-form__price'>
            {item.originalPrice !== undefined && (
              <span className='order-quantity-form__price--strikethrough'>
                {item.originalPrice.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}{' '}
              </span>
            )}
            {item.discountPercent === 100 ? (
              <span className='order-quantity-form__price--free'>0.00</span>
            ) : (
              <span>
                {item.price.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </span>
            )}{' '}
            {t('Promotion Detail.label_THB')}
          </div>
        </div>
      ))}

      <div className='order-quantity-form__total'>
        {t('eGift : Purchase.ONLINEGIFT_total_price')}:&nbsp;
        <span className='order-quantity-form__total-amount'>
          {totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}{' '}
          {t('Promotion Detail.label_THB')}
        </span>
      </div>
    </div>
  )
}

export default OrderQuantityForm
